import {
  Auth,
  browserLocalPersistence,
  GoogleAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  Unsubscribe,
  User,
} from "firebase/auth"

import { ErrorType, formatError } from "#utils/textFormatters"

import Firebase from "./Firebase"
import FirebaseAnalytics from "./FirebaseAnalytics"

class FirebaseAuthSingleton {
  private readonly auth: Auth
  private readonly googleProvider: GoogleAuthProvider
  public user: User | null = null

  constructor(auth?: Auth) {
    this.auth = auth ?? Firebase.getInstance().auth
    this.googleProvider = new GoogleAuthProvider()
  }

  setAuthStateListener = (
    callback: (user: User | null) => void,
    errorCallback?: (error: string) => void
  ): Unsubscribe => {
    return onAuthStateChanged(
      this.auth,
      (user) => {
        this.user = user
        console.log("✅ PIN login successful!")

        // Track user login/logout
        if (user) {
          FirebaseAnalytics.getInstance().setUserId(user.uid)
          FirebaseAnalytics.getInstance().setUserProperties({
            email: user.email,
            display_name: user.displayName,
            email_verified: user.emailVerified,
          })
        } else {
          FirebaseAnalytics.getInstance().setUserId("")
        }

        callback(user)
      },
      (error) => {
        console.error("❌ Login failed error => ", error)
        FirebaseAnalytics.getInstance().trackError(
          error as Error,
          "auth_state_change"
        )
        errorCallback?.(formatError(error as ErrorType))
      }
    )
  }

  signInWithEmailAndPassword = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(
        this.auth,
        email,
        password
      )
      this.user = result.user
      setPersistence(this.auth, browserLocalPersistence)

      // Track successful login
      FirebaseAnalytics.getInstance().trackUserLogin("email")

      return result
    } catch (error) {
      console.error("❌🔥 signInWithEmailAndPassword error => ", error)
      FirebaseAnalytics.getInstance().trackError(error as Error, "email_login")
      throw error
    }
  }

  signInWithGoogle = async () => {
    return signInWithPopup(this.auth, this.googleProvider)
      .then(() => {
        this.user = this.auth.currentUser

        // Track successful Google login
        FirebaseAnalytics.getInstance().trackUserLogin("google")
      })
      .catch((error) => {
        console.error("❌🔥 signInWithGoogle error => ", error)
        FirebaseAnalytics.getInstance().trackError(
          error as Error,
          "google_login"
        )
      })
  }

  signOut = async () => {
    // Track logout before signing out
    FirebaseAnalytics.getInstance().trackUserLogout()

    await signOut(this.auth)
    await Firebase.getInstance().terminateFirebase()
  }
}

class FirebaseAuth {
  private static instance: FirebaseAuthSingleton | null = null

  constructor(auth: Auth) {
    if (FirebaseAuth.instance == null) {
      FirebaseAuth.instance = new FirebaseAuthSingleton(auth)
    }
  }

  public static getInstance(): FirebaseAuthSingleton {
    if (FirebaseAuth.instance == null) {
      FirebaseAuth.instance = new FirebaseAuthSingleton(
        Firebase.getInstance().auth
      )
    }
    return FirebaseAuth.instance
  }
}

export default FirebaseAuth
